import type { ColumnValue } from '@opengov/reporting-types';
import type { ColDef } from 'ag-grid-community';
import { isEqual } from 'lodash';

/** The route loader builds the grid's columns; the query effect would build identical ones moments later. */
const MEMO_LIFETIME_MS = 10_000;

export interface LoadedColumns {
  agGridColumns: ColDef[];
  columnValues: ColumnValue[];
}

interface MemoEntry {
  reportConfigurationId: string;
  accountSummary: unknown;
  isEditing: boolean;
  columns: LoadedColumns;
  rememberedAt: number;
}

let memo: MemoEntry | null = null;

export function rememberLoadedColumns(entry: Omit<MemoEntry, 'rememberedAt'>): void {
  memo = { ...entry, rememberedAt: Date.now() };
}

/**
 * Returns the columns the loader just built, once, when they are exactly what the caller would
 * build: same configuration, same account summary, same mode, and recent. Anything else (an edit
 * in between, a later effect run) gets null and builds its own columns as usual.
 */
export function takeReusableColumns(request: {
  reportConfigurationId: string;
  accountSummary: unknown;
  isEditing: boolean;
}): LoadedColumns | null {
  const entry = memo;
  memo = null;
  if (!entry) return null;

  const isRecent = Date.now() - entry.rememberedAt <= MEMO_LIFETIME_MS;
  const isSameRequest =
    entry.reportConfigurationId === request.reportConfigurationId &&
    entry.isEditing === request.isEditing &&
    isEqual(entry.accountSummary, request.accountSummary);

  return isRecent && isSameRequest ? entry.columns : null;
}

/**
 * The pivot cache is fresh for a report open: the route loader has just requested its columns, and
 * its rows are being prefetched. The query effect runs more than once per open (the saved view is
 * selected right after the configuration is set), and every one of those runs may keep the cache.
 * Unlike the columns above, this is not taken once.
 */
interface FreshOpen {
  reportConfigurationId: string;
  accountSummary: unknown;
  openedAt: number;
}

let freshOpen: FreshOpen | null = null;

export function rememberFreshOpen(open: Omit<FreshOpen, 'openedAt'>): void {
  freshOpen = { ...open, openedAt: Date.now() };
}

/**
 * True for a query run that is part of the open the loader just made: the same configuration and
 * account summary, within ten seconds. Anything later, such as a user's edit, or anything different
 * wipes the cache as it always did, so the grid never shows an answer from before the action.
 */
export function isStillFreshOpen(request: { reportConfigurationId: string; accountSummary: unknown }): boolean {
  if (!freshOpen) return false;
  const isRecent = Date.now() - freshOpen.openedAt <= MEMO_LIFETIME_MS;
  return (
    isRecent &&
    freshOpen.reportConfigurationId === request.reportConfigurationId &&
    isEqual(freshOpen.accountSummary, request.accountSummary)
  );
}
